import { useMutation } from '@apollo/client';
import { useCallback, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import {
    ClaimPrescriberOrderForPatientDocument,
    type PatientInfoFragment,
} from '../../../graphql/generated/graphql';
import { usePatientContext } from '../../../hooks/use-patient-context';
import { usePrescriberOrder } from '../../../hooks/use-prescriber-order';
import { useRefetchQueries } from '../../../hooks/use-refetch-queries';
import { getAbsoluteRoute } from '../../../util/get-absolute-route';
import { getRefetchQueryForPrescription } from '../../../util/get-refetch-query-for-prescription';

import { VerifyPatient } from './verify/verify-patient/VerifyPatient';

export const VerifyPatientPage = () => {
    const { order, orderId } = usePrescriberOrder();
    const { patient } = usePatientContext();
    const navigate = useNavigate();

    const { refetch, isComplete: isRefetchComplete } = useRefetchQueries(
        getRefetchQueryForPrescription(orderId, patient.id)
    );

    useEffect(() => {
        if (isRefetchComplete) {
            navigate(
                getAbsoluteRoute('order.prescriberOrderId.details', {
                    prescriberOrderId: orderId,
                })
            );
        }
    }, [navigate, isRefetchComplete, orderId]);

    const [ClaimPrescriberOrderForPatient] = useMutation(
        ClaimPrescriberOrderForPatientDocument
    );

    const handleClaimPrescription = useCallback(
        async (patient: PatientInfoFragment) => {
            const result = await ClaimPrescriberOrderForPatient({
                variables: {
                    patientId: patient.id,
                    prescriberOrderId: orderId,
                },
            });

            if (result.data?.claimPrescriberOrderForPatient.success) {
                await refetch();
            }
        },
        [ClaimPrescriberOrderForPatient, orderId, refetch]
    );

    if (order.__typename !== 'UnclaimedPrescriberOrder') {
        return (
            <Navigate
                to={getAbsoluteRoute('order.prescriberOrderId.details', {
                    prescriberOrderId: orderId,
                })}
            />
        );
    }

    if (order.matchingPatientsConnection?.edges.length == 0) {
        return (
            <Navigate
                to={getAbsoluteRoute('order.prescriberOrderId.verify', {
                    prescriberOrderId: orderId,
                })}
                replace
            />
        );
    }

    return (
        <VerifyPatient
            prescriberOrder={order}
            claimPrescription={handleClaimPrescription}
        />
    );
};
